import { BASES_COM_MATERIAL, idDaVariante, materiaisDa, MATERIAIS, type FamiliaMaterial } from '../look/materiais';
import { MONTARIAS } from '../world/montarias';
import type { CatalogDef, ItemCatalog, ItemGroup } from './types';

type Seed = readonly [id: string, name: string, group: string, stackable: boolean, weight: number];

const GROUP_ORDER = ['weapon', 'armor', 'shield', 'cloth', 'mount', 'ammo', 'ore', 'ingot', 'potion', 'misc', 'coleta'] as const;
const GROUP_LABEL: Record<(typeof GROUP_ORDER)[number], string> = {
  weapon: 'Armas',
  armor: 'Armaduras',
  shield: 'Escudos',
  cloth: 'Roupas',
  mount: 'Animais',
  ammo: 'Munição',
  ore: 'Minérios',
  ingot: 'Lingotes',
  potion: 'Poções',
  misc: 'Outros',
  coleta: 'Coleta'
};

const SEED: readonly Seed[] = [
  ['arrow', 'Flecha', 'ammo', true, 0.1],
  ['bolt', 'Virote', 'ammo', true, 0.1],
  ['iron-ore', 'Iron Ore', 'ore', true, 0.1],
  ['dull-copper-ore', 'Dull Copper Ore', 'ore', true, 0.1],
  ['shadow-iron-ore', 'Shadow Iron Ore', 'ore', true, 0.1],
  ['copper-ore', 'Copper Ore', 'ore', true, 0.1],
  ['bronze-ore', 'Bronze Ore', 'ore', true, 0.1],
  ['golden-ore', 'Golden Ore', 'ore', true, 0.1],
  ['agapite-ore', 'Agapite Ore', 'ore', true, 0.1],
  ['verite-ore', 'Verite Ore', 'ore', true, 0.1],
  ['valorite-ore', 'Valorite Ore', 'ore', true, 0.1],
  ['iron-ingot', 'Iron Ingot', 'ingot', true, 0],
  ['dull-copper-ingot', 'Dull Copper Ingot', 'ingot', true, 0],
  ['shadow-iron-ingot', 'Shadow Iron Ingot', 'ingot', true, 0],
  ['copper-ingot', 'Copper Ingot', 'ingot', true, 0],
  ['bronze-ingot', 'Bronze Ingot', 'ingot', true, 0],
  ['golden-ingot', 'Golden Ingot', 'ingot', true, 0],
  ['agapite-ingot', 'Agapite Ingot', 'ingot', true, 0],
  ['verite-ingot', 'Verite Ingot', 'ingot', true, 0],
  ['valorite-ingot', 'Valorite Ingot', 'ingot', true, 0],
  ['gold', 'Ouro', 'misc', true, 0.02],
  ['silver', 'Prata', 'misc', true, 0.02],
  ['bandage', 'Bandagem', 'misc', true, 0.1],
  ['greater-healing', 'Poção de vida maior', 'potion', true, 0.4],
  ['cure-poison', 'Poção de cura veneno', 'potion', true, 0.4],
  ['greater-mana', 'Poção de mana maior', 'potion', true, 0.4],
  ['greater-cure', 'Poção de cura veneno maior', 'potion', true, 0.4],
  ['refresh', 'Poção de vigor', 'potion', true, 0.4],
  ['total-refresh', 'Poção de vigor maior', 'potion', true, 0.4],
  ['strength-potion', 'Poção de força', 'potion', true, 0.4],
  ['greater-strength', 'Poção de força maior', 'potion', true, 0.4],
  ['agility-potion', 'Poção de agilidade', 'potion', true, 0.4],
  ['greater-agility', 'Poção de agilidade maior', 'potion', true, 0.4],
  ['nightsight', 'Poção de visão noturna', 'potion', true, 0.4],
  ['magic-resist', 'Poção de resistência mágica', 'potion', true, 0.4],
  ['empty-bottle', 'Garrafa vazia', 'misc', true, 0.4],
  ['mortar-pestle', 'Almofariz e pilão', 'misc', false, 1],
  // Couro e osso por material (proposta-armaduras-couro-e-osso §01): os 8 crus; nome e peso iguais ao servidor.
  ['leather', 'Couro', 'misc', true, 0.1],
  ['spined-leather', 'Couro Espinhoso', 'misc', true, 0.1],
  ['horned-leather', 'Couro Chifrudo', 'misc', true, 0.1],
  ['barbed-leather', 'Couro Farpado', 'misc', true, 0.1],
  ['bone', 'Osso', 'misc', true, 0.1],
  ['troll-bone', 'Osso de Troll', 'misc', true, 0.1],
  ['daemon-bone', 'Osso de Daemon', 'misc', true, 0.1],
  ['dragon-bone', 'Osso de Dragão', 'misc', true, 0.1],
  ['cloth', 'Pano', 'misc', true, 0.1],
  ['sewing-kit', 'Kit de costura', 'misc', false, 2],
  // Aba própria no menu de GM, como o GmVitrine do servidor.
  ['hatchet', 'Machadinha', 'coleta', false, 4],
  ['golden-hatchet', 'Machadinha de Ouro', 'coleta', false, 4],
  ['pickaxe', 'Picareta', 'coleta', false, 4],
  ['golden-pickaxe', 'Picareta de Ouro', 'coleta', false, 4],
  ['log', 'Tora', 'misc', true, 0],
  ['oak-log', 'Oak Log', 'misc', true, 0],
  ['ash-log', 'Ash Log', 'misc', true, 0],
  ['yew-log', 'Yew Log', 'misc', true, 0],
  ['heartwood-log', 'Heartwood Log', 'misc', true, 0],
  ['bloodwood-log', 'Bloodwood Log', 'misc', true, 0],
  ['frostwood-log', 'Frostwood Log', 'misc', true, 0],
  ['shaft', 'Haste', 'misc', true, 0.1],
  ['feather', 'Pena', 'misc', true, 0.1],
  ['fletcher-tools', 'Ferramentas de arqueiro', 'misc', false, 2],
  ['spellbook', 'Livro de magias', 'misc', false, 1],
  ['scroll', 'Pergaminho', 'misc', true, 0.1],
  // O guarda-roupa (techspec-guardaroupa): saque que vira cor no traje.
  ['tinta-prime-fogo', 'Tinta prime · Fogo', 'misc', true, 0.1],
  ['tinta-prime-gelo', 'Tinta prime · Gelo', 'misc', true, 0.1],
  ['tinta-prime-arcano', 'Tinta prime · Arcano', 'misc', true, 0.1],
  ['tinta-prime-neve', 'Tinta prime · Neve', 'misc', true, 0.1],
  ['tinta-prime-sombra', 'Tinta prime · Sombra', 'misc', true, 0.1],
  ['tinta-prime-ouro', 'Tinta prime · Ouro', 'misc', true, 0.1],
  ['tinta-prime-negro', 'Tinta prime · Negro', 'misc', true, 0.1],
  ['tinta-prime-sangue', 'Tinta prime · Sangue', 'misc', true, 0.1],
  ['tinta-prime-veneno', 'Tinta prime · Veneno', 'misc', true, 0.1],
  ['tinta-prime-purpura', 'Tinta prime · Púrpura', 'misc', true, 0.1],
  ['tinta-prime-carmim', 'Tinta prime · Carmim', 'misc', true, 0.1],
  // As básicas trancadas (Renato, 21/10): a tinta comum cai do Baú do Balron.
  ['tinta-carvao', 'Tinta · Carvão', 'misc', true, 0.1],
  ['tinta-vinho', 'Tinta · Vinho', 'misc', true, 0.1],
  ['tinta-vermelho', 'Tinta · Vermelho', 'misc', true, 0.1],
  ['tinta-mostarda', 'Tinta · Mostarda', 'misc', true, 0.1],
  ['tinta-musgo', 'Tinta · Musgo', 'misc', true, 0.1],
  ['tinta-verde', 'Tinta · Verde', 'misc', true, 0.1],
  ['tinta-anil', 'Tinta · Anil', 'misc', true, 0.1],
  ['tinta-roxo', 'Tinta · Roxo', 'misc', true, 0.1],
  ['heater', 'Escudo heater', 'shield', false, 8],
  // As 117 montarias (world/montarias.ts): 13 bichos × cor original + 8 tintas prime.
  ...MONTARIAS.map((m): Seed => [m.id, m.nome, 'mount', false, 1]),
  ['katana', 'Katana', 'weapon', false, 6],
  ['dagger', 'Adaga', 'weapon', false, 1],
  ['large-battle-axe', 'Machado de batalha grande', 'weapon', false, 11],
  ['gnarled-staff', 'Bastão retorcido', 'weapon', false, 3],
  ['bow', 'Arco', 'weapon', false, 6],
  ['heavy-crossbow', 'Besta pesada', 'weapon', false, 11],
  ['studded-helm', 'Elmo de couro cravejado', 'armor', false, 1],
  ['studded-chest', 'Peitoral de couro cravejado', 'armor', false, 5],
  ['studded-arms', 'Braçadeiras de couro cravejado', 'armor', false, 3],
  ['studded-gloves', 'Luvas de couro cravejado', 'armor', false, 1],
  ['studded-legs', 'Perneiras de couro cravejado', 'armor', false, 5],
  ['bone-helm', 'Elmo de osso', 'armor', false, 3],
  ['bone-chest', 'Peitoral de osso', 'armor', false, 6],
  ['bone-arms', 'Braçadeiras de osso', 'armor', false, 3],
  ['bone-gloves', 'Luvas de osso', 'armor', false, 2],
  ['bone-legs', 'Perneiras de osso', 'armor', false, 5],
  ['plate-helm', 'Elmo de placas', 'armor', false, 5],
  ['plate-chest', 'Peitoral de placas', 'armor', false, 10],
  ['plate-arms', 'Braçadeiras de placas', 'armor', false, 5],
  ['plate-gloves', 'Luvas de placas', 'armor', false, 2],
  ['plate-legs', 'Perneiras de placas', 'armor', false, 7]
];

const METAIS_GM = MATERIAIS.filter(m => m.familia === 'metal').map(m => m.id);
/** Armas com variante de material (techspec-materiais); a besta pesada fica de fora. */
const ARMAS_COM_MATERIAL: Readonly<Record<string, FamiliaMaterial>> = {
  dagger: 'metal', katana: 'metal', 'large-battle-axe': 'metal', bow: 'madeira', 'gnarled-staff': 'madeira'
};

function pesoFamilia(familia: string): number {
  return SEED.filter(row => row[2] === 'armor' && row[0].startsWith(`${familia}-`)).reduce((s, row) => s + row[4], 0);
}

function nomeMaterial(id: string): string {
  return MATERIAIS.find(m => m.id === id)?.nome ?? id;
}

function kitsDeArmadura(): CatalogDef[] {
  const kits: CatalogDef[] = [
    { id: 'gmkit:studded', name: 'Couro cravejado', stackable: false, weight: pesoFamilia('studded'), price: 0, equipSlot: 'chest' },
    ...materiaisDa('couro').filter(m => !m.padrao).map(m => ({
      id: `gmkit:studded@${m.id}`, name: `Couro cravejado de ${m.nome}`, stackable: false, weight: pesoFamilia('studded'),
      price: 0, equipSlot: 'chest', material: m.id
    })),
    { id: 'gmkit:bone', name: 'Osso', stackable: false, weight: pesoFamilia('bone'), price: 0, equipSlot: 'chest' },
    ...materiaisDa('osso').filter(m => !m.padrao).map(m => ({
      id: `gmkit:bone@${m.id}`, name: `Osso · ${m.nome}`, stackable: false, weight: pesoFamilia('bone'),
      price: 0, equipSlot: 'chest', material: m.id
    }))
  ];
  for (const metal of METAIS_GM) {
    kits.push({
      id: `gmkit:plate@${metal}`,
      name: `${nomeMaterial(metal)} Plate`,
      stackable: false,
      weight: pesoFamilia('plate'),
      price: 0,
      equipSlot: 'chest',
      material: metal
    });
  }
  return kits;
}

/** Cada arma seguida das variantes da família dela, como o `GmVitrine` do servidor. */
function armasDoGm(base: CatalogDef[]): CatalogDef[] {
  const linhas: CatalogDef[] = [];
  for (const arma of base) {
    const familia = ARMAS_COM_MATERIAL[arma.id];
    if (!familia) {
      linhas.push(arma);
      continue;
    }
    for (const m of materiaisDa(familia)) {
      linhas.push(m.padrao
        ? { ...arma, material: m.id }
        : { ...arma, id: idDaVariante(arma.id, m), name: `${arma.name} de ${m.nome}`, material: m.id });
    }
  }
  return linhas;
}

export const MOCK_ITEM_CATALOG: ItemCatalog = {
  groups: GROUP_ORDER.map(id => {
    const items: CatalogDef[] = SEED
      .filter(row => row[2] === id)
      .map(([itemId, name, group, stackable, weight]) => ({
        id: itemId, name, stackable, weight, price: 0, equipSlot: slotOf(itemId, group, stackable)
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    if (id === 'armor') return { id, name: GROUP_LABEL[id], items: kitsDeArmadura() } satisfies ItemGroup;
    if (id === 'weapon') return { id, name: GROUP_LABEL[id], items: armasDoGm(items) } satisfies ItemGroup;
    return { id, name: GROUP_LABEL[id], items } satisfies ItemGroup;
  })
};

function defDaSemente(row: Seed): CatalogDef {
  const [itemId, name, group, stackable, weight] = row;
  return { id: itemId, name, stackable, weight, price: 0, equipSlot: slotOf(itemId, group, stackable) };
}

/** Cada material é um item próprio: as variantes nascem iguais à base, com o nome do material. */
function variantesDaSemente(): (readonly [string, CatalogDef])[] {
  const out: (readonly [string, CatalogDef])[] = [];
  for (const row of SEED) {
    const familia = BASES_COM_MATERIAL[row[0]];
    if (!familia) continue;
    const base = defDaSemente(row);
    for (const m of materiaisDa(familia)) {
      if (m.padrao) continue;
      const id = idDaVariante(row[0], m);
      out.push([id, { ...base, id, name: `${row[1]} de ${m.nome}`, material: m.id }] as const);
    }
  }
  return out;
}

export const MOCK_ITEM_DEFS = new Map<string, CatalogDef>([
  ...SEED.map(row => [row[0], defDaSemente(row)] as const),
  ...variantesDaSemente(),
  ...MOCK_ITEM_CATALOG.groups.flatMap(g => g.items).map(d => [d.id, d] as const)
]);

const PECAS_KIT = ['helm', 'chest', 'arms', 'gloves', 'legs'] as const;

/** Peças que o kit do menu de GM entrega: a variante do metal (`plate-helm@valorite`). */
export function pecasDoKitGm(kitId: string): { defId: string; name: string; weight: number }[] {
  if (!kitId.startsWith('gmkit:')) return [];
  const corpo = kitId.slice('gmkit:'.length);
  const corte = corpo.indexOf('@');
  const familia = corte < 0 ? corpo : corpo.slice(0, corte);
  const doKit: Record<string, FamiliaMaterial> = { plate: 'metal', studded: 'couro', bone: 'osso' };
  const fam = doKit[familia];
  if (!fam) return [];
  const padrao = materiaisDa(fam).find(m => m.padrao)?.id ?? '';
  const metal = corte < 0 ? padrao : corpo.slice(corte + 1);
  if (!materiaisDa(fam).some(m => m.id === metal)) return [];
  return SEED
    .filter(row => row[2] === 'armor' && PECAS_KIT.some(p => row[0] === `${familia}-${p}`))
    .map(row => ({
      defId: idDaVariante(row[0], metal),
      name: metal === padrao ? row[1] : `${row[1]} de ${nomeMaterial(metal)}`,
      weight: row[4]
    }));
}

export function pesoDoItem(id: string): number | undefined {
  return MOCK_ITEM_DEFS.get(id)?.weight;
}

const TWO_HAND = new Set<string>([
  'large-battle-axe', 'gnarled-staff', 'bow', 'heavy-crossbow', 'hatchet', 'golden-hatchet', 'pickaxe', 'golden-pickaxe'
]);

const STR_REQ: Record<string, number> = {
  katana: 10,
  dagger: 1,
  'large-battle-axe': 40,
  'gnarled-staff': 10,
  bow: 20,
  'heavy-crossbow': 40
};

export function mockHands(defId: string): number {
  return TWO_HAND.has(defId) ? 2 : 1;
}

export function mockStrReq(defId: string): number {
  return STR_REQ[defId] ?? 0;
}

export function wornSlot(equipSlot: string): string | null {
  if (equipSlot === 'none' || !equipSlot) return null;
  if (equipSlot === 'weapon') return 'mainHand';
  if (equipSlot === 'shield') return 'offHand';
  return equipSlot;
}

function slotOf(id: string, group: string, stackable: boolean): string {
  if (stackable) return 'none';
  if (group === 'weapon') return 'weapon';
  // A machadinha e a picareta da coleta vão no slot de arma (dano 0), embora não sejam armas de combate.
  if (id === 'hatchet' || id === 'golden-hatchet' || id === 'pickaxe' || id === 'golden-pickaxe') return 'weapon';
  if (group === 'shield') return 'shield';
  if (group === 'cloth') return 'cloth';
  if (group === 'mount') return 'mount';
  if (group === 'armor') {
    const piece = id.slice(id.lastIndexOf('-') + 1);
    if (piece === 'helm' || piece === 'chest'
      || piece === 'arms' || piece === 'gloves' || piece === 'legs') return piece;
  }
  return 'none';
}
